'use client';

import type { ComponentProps, ElementType, ReactNode } from 'react';
import { twMerge } from 'tailwind-merge';

/**
 * The site's one button (refined in spec 013).
 *
 * Every clickable rectangle — the header's "Napisz do nas", a form's submit,
 * a page control, a link styled as a button — comes through here so hover,
 * pressed, pending and disabled all feel the same. Polymorphic through `as`,
 * so a `Link` gets the same face as a `button`. `ref` is an ordinary prop
 * (React 19), so a caller can return focus to it.
 *
 *   primary    gold fill
 *   secondary  gold outline; on hover it fills faintly
 *   ghost      no border; for controls inside a coloured surface
 *   blacked    dark fill for use ON the gold surface
 *
 * The hover is a SHEEN: a narrow streak of light, skewed to the same angle as
 * the cards' diagonal cut, sweeps across the face once — light moving over
 * paint — while the fill warms a touch. Nothing else moves: the fill never
 * flips to an outline (that read as a state change, not a response), the
 * button never lifts or scales, and the label and icons hold still. Tracking
 * in particular stays fixed — a label that widens grows an inline button
 * with it, which reads as a scale, and re-centres on a full-width one.
 * `loading` swaps the leading icon for a spinner. Reduced motion keeps the
 * colours only.
 */

type TButtonProps<T extends ElementType> = {
  as?: T;
  variant?: 'primary' | 'secondary' | 'ghost';
  size?: 'sm' | 'md';
  /** A boxed icon at the leading edge — the hero's calculator button. */
  icon?: ReactNode;
  iconOnly?: boolean;
  /** Inline icons beside the label. `loading` replaces the leading one with a spinner. */
  startIcon?: ReactNode;
  endIcon?: ReactNode;
  /** Extra classes for the label span — a caller can send it the other way. */
  labelClassName?: string;
  loading?: boolean;
  className?: string;
  children?: ReactNode;
  blacked?: boolean;
} & Omit<ComponentProps<T>, 'as'>;

import { SEAM } from './seam';

export { SEAM };


const Spinner = () => (
  <span
    aria-hidden
    className="inline-block h-[1.1em] w-[1.1em] shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
  />
);

export const Button = <T extends ElementType = 'button'>({
  as,
  variant = 'primary',
  size = 'md',
  iconOnly = false,
  icon,
  startIcon,
  endIcon,
  labelClassName,
  className,
  loading = false,
  children,
  disabled,
  blacked = false,
  ...rest
}: TButtonProps<T>) => {
  const Component = as || 'button';
  const isInert = Boolean(disabled) || loading;

  const base = twMerge(
    'group relative inline-flex select-none items-center justify-center gap-2 overflow-hidden whitespace-nowrap rounded-md font-oswald',
    'transition-[transform,box-shadow,background-color,color,border-color,opacity] duration-300 ease-out',

    // The sheen. Parked off the left edge, skewed like the card cut; on hover it
    // crosses the face in half a second and parks off the right edge.
    'before:pointer-events-none before:absolute before:inset-y-0 before:-left-1/2 before:w-2/5 before:-skew-x-[20deg] before:bg-gradient-to-r before:from-transparent before:to-transparent before:opacity-0 before:transition-[transform,opacity] before:duration-[550ms] before:ease-out motion-safe:hover:before:translate-x-[380%] hover:before:opacity-100',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-action/70 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    size === 'sm' ? 'h-10 text-sm tracking-wide' : 'h-12 text-base',
    !icon && (size === 'sm' ? 'px-4' : 'px-6')
  );

  const face = blacked
    ? 'border border-background bg-background text-action before:via-white/10 hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.08)]'
    : variant === 'primary'
      ? 'border border-action bg-action text-background before:via-white/40 hover:bg-[#fcc61f] hover:shadow-[inset_0_1px_0_rgba(255,255,255,0.35),0_10px_24px_-12px_rgba(251,192,11,0.6)]'
      : variant === 'secondary'
        ? 'border border-action/40 bg-transparent text-action before:via-action/20 hover:border-action/80 hover:bg-action/[0.06]'
        : 'border border-transparent bg-transparent text-foreground/80 before:via-white/10 hover:bg-foreground/5 hover:text-foreground';

  const iconBox =
    variant === 'primary'
      ? 'border border-action bg-action text-background group-hover:bg-transparent group-hover:text-action'
      : 'border border-neutral-600 bg-transparent text-neutral-400 group-hover:border-action group-hover:bg-action/10 group-hover:text-action';

  // A boxed-icon button has no face of its own — no outline, a white label
  // that does nothing on hover. The response lives in the ICON BOX alone: it
  // colours as it always did, and the same sheen every button has sweeps the
  // box instead of the face (2026-09-14).
  const iconFace = 'text-foreground before:hidden';

  const iconBoxSheen =
    'before:pointer-events-none before:absolute before:inset-y-0 before:-left-1/2 before:w-2/5 before:-skew-x-[20deg] before:bg-gradient-to-r before:from-transparent before:via-action/30 before:to-transparent before:opacity-0 before:transition-[transform,opacity] before:duration-[550ms] before:ease-out motion-safe:group-hover:before:translate-x-[380%] group-hover:before:opacity-100';

  const classes = twMerge(
    base,
    icon ? iconFace : face,
    // A lone icon is a PILL — fully rounded and wider than tall, the shape the
    // gallery chevrons always had — while a labelled button keeps the header's
    // soft corners.
    iconOnly && 'rounded-full px-5',
    isInert && 'cursor-not-allowed opacity-70 hover:shadow-none',
    className
  );

  const leading = loading ? <Spinner /> : startIcon;

  return (
    <Component
      className={classes}
      disabled={Component === 'button' ? isInert : undefined}
      aria-busy={loading || undefined}
      {...rest}
    >
      {icon ? (
        <span
          className={twMerge(
            'relative z-10 flex h-12 w-12 flex-none items-center justify-center overflow-hidden rounded-md transition-all duration-300',
            iconBoxSheen,
            iconBox
          )}
        >
          <span className="relative z-10 flex">{loading ? <Spinner /> : icon}</span>
        </span>
      ) : (
        leading && <span className="relative z-10 flex shrink-0 items-center">{leading}</span>
      )}
      {!iconOnly && (
        <span
          className={twMerge(
            'relative z-10 tracking-wide',
            icon && 'pl-2 pr-5',
            loading && 'opacity-80',
            labelClassName
          )}
        >
          {children}
        </span>
      )}
      {!icon && endIcon && <span className="relative z-10 flex shrink-0 items-center">{endIcon}</span>}
    </Component>
  );
};
